/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { InfoPopover } from "components/InfoPopover"
import InputError from "components/InputError"
import { CurrentUserParameters } from "features/userParameters/UserParametersSchemata"
import { FC, useEffect } from "react"
import DatePicker from "react-datepicker"
import {
  Controller,
  ControllerProps,
  useFormContext,
  UseFormRegister,
  useWatch,
} from "react-hook-form"
import { toDisplayDate } from "utils/dateUtils"

// AIDEV-NOTE: Preserve existing prop contracts and only narrow readonly-lint boundaries where react-hook-form/date-picker callbacks cross library types.
export interface CashFlowProps {
  readonly index: number
  readonly register: UseFormRegister<CurrentUserParameters>
  // AIDEV-NOTE: `remove` mirrors react-hook-form's useFieldArray signature exactly.
  // Narrowing to `readonly number[]` would make RHF's own remove unassignable here
  // under strictFunctionTypes, so the suppression stays.
  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  remove: (index?: number | number[]) => void
}

type CashFlowStartRenderArgs = Parameters<
  NonNullable<ControllerProps<CurrentUserParameters, `cashFlows.${number}.start`>["render"]>
>[0]
type CashFlowEndRenderArgs = Parameters<
  NonNullable<ControllerProps<CurrentUserParameters, `cashFlows.${number}.end`>["render"]>
>[0]

const CashFlow: FC<Readonly<CashFlowProps>> = ({ index, register, remove }) => {
  const {
    control,
    trigger,
    watch,
    clearErrors,
    unregister,
    formState: { errors },
  } = useFormContext<CurrentUserParameters>()

  const frequencyValue = useWatch({
    control,
    name: `cashFlows.${index}.frequency`,
  })

  useEffect(() => {
    if (frequencyValue === "one-time") {
      unregister(`cashFlows.${index}.end`)
    }
  }, [frequencyValue, unregister, index])

  const retirementStart = watch("retirementStart")
  const retirementHorizonMonths = watch("retirementHorizonMonths")

  // ensures an error is shown for the end date if the retirement length is changed
  // such that the end date follows the end of the retirement period, or, for a one-time
  // flow (which has no end date), for the start date if it follows that end. It also
  // re-runs when the frequency changes and re-validates the start whichever way it
  // changed: to raise the one-time-only start error for a flow switched to one-time while
  // already past the retirement end, and to clear it for a flow switched away from one-time
  useEffect(() => {
    const endValue = watch(`cashFlows.${index}.end`)
    if (endValue) {
      void trigger(`cashFlows.${index}.end`)
    }
    const startValue = watch(`cashFlows.${index}.start`)
    // during a form reset from a parameters file upload
    // the start value may be undefined, perhaps momentarily
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (startValue) {
      void trigger(`cashFlows.${index}.start`)
    }
  }, [retirementHorizonMonths, frequencyValue, trigger, index, watch, clearErrors])

  // ensures an error is shown for the start date if the retirement start date is changed to follow it
  // or if it's changed such that the end date follows the end of the retirement period
  useEffect(() => {
    const startValue = watch(`cashFlows.${index}.start`)
    // during a form reset from a parameters file upload
    // the start value may be undefined, perhaps momentarily
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (startValue) {
      void trigger(`cashFlows.${index}.start`)
    }
    const endValue = watch(`cashFlows.${index}.end`)
    if (endValue) {
      void trigger(`cashFlows.${index}.end`)
    }
  }, [retirementStart, trigger, index, watch, clearErrors])

  // Function to convert number to ordinal string (1st, 2nd, 3rd, etc.)
  const getOrdinal = (n: number): string => {
    const suffixes = ["th", "st", "nd", "rd"]
    const v = n % 100
    return n.toString() + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0])
  }

  return (
    <div className="cash-flow-section" data-index={index}>
      <h5>{getOrdinal(index + 1)} Cash Flow</h5>
      <div className="row py-1">
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor={`flow-${index}-name`} className="col-form-label">
            Name
          </label>
          <div className="input-group">
            <input
              type="text"
              inputMode="text"
              className={`form-control${errors.cashFlows?.[index]?.name ? " is-invalid" : ""}`}
              id={`flow-${index}-name`}
              aria-invalid={!!errors.cashFlows?.[index]?.name}
              {...register(`cashFlows.${index}.name`)}
            />
            <InputError error={errors.cashFlows?.[index]?.name} />
          </div>
        </div>
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor={`flow-${index}-amount`} className="col-form-label">
            Amount
          </label>
          <div className="input-group">
            <span className="input-group-text">$</span>
            <input
              type="text"
              inputMode="decimal"
              className={`form-control${errors.cashFlows?.[index]?.amount ? " is-invalid" : ""}`}
              id={`flow-${index}-amount`}
              aria-invalid={!!errors.cashFlows?.[index]?.amount}
              {...register(`cashFlows.${index}.amount`)}
            />
            <InputError error={errors.cashFlows?.[index]?.amount} />
          </div>
        </div>
        <fieldset className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <legend className="col-form-label">Frequency</legend>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              id={`flow-${index}-annual-frequency`}
              value="annually"
              {...register(`cashFlows.${index}.frequency`)}
            />
            <label className="form-check-label" htmlFor={`flow-${index}-annual-frequency`}>
              Annually
            </label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              id={`flow-${index}-monthly-frequency`}
              value="monthly"
              {...register(`cashFlows.${index}.frequency`)}
            />
            <label className="form-check-label" htmlFor={`flow-${index}-monthly-frequency`}>
              Monthly
            </label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              id={`flow-${index}-one-time-frequency`}
              value="one-time"
              {...register(`cashFlows.${index}.frequency`)}
            />
            <label className="form-check-label" htmlFor={`flow-${index}-one-time-frequency`}>
              One-Time
            </label>
          </div>
        </fieldset>
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor={`flow-${index}-inflation-adjusted`} className="col-form-label">
            Inflation Adjusted
          </label>
          <div className="input-group">
            <input
              type="checkbox"
              className="form-check-input"
              id={`flow-${index}-inflation-adjusted`}
              {...register(`cashFlows.${index}.inflationAdjusted`)}
            />
          </div>
        </div>
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
          <label htmlFor={`flow-${index}-start`} className="col-form-label">
            Start
          </label>
          <Controller
            name={`cashFlows.${index}.start`}
            control={control}
            // AIDEV-NOTE: react-hook-form owns this render-arg shape; making it readonly
            // makes `error` unassignable to InputError.
            // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
            render={(controllerProps: CashFlowStartRenderArgs) => {
              const {
                field,
                fieldState: { error },
              } = controllerProps
              return (
                <>
                  <div className={`input-group ${error ? "is-invalid" : ""}`}>
                    <DatePicker
                      id={`flow-${index}-start`}
                      // AIDEV-NOTE: react-datepicker hands back its own component instance here.
                      // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
                      ref={(elem: DatePicker | null) => {
                        if (elem) {
                          field.ref(elem.input)
                        }
                      }}
                      className={`form-control ${error ? "is-invalid" : ""}`}
                      dateFormat="MM/yyyy"
                      showMonthYearPicker
                      autoComplete="off"
                      placeholderText="mm/yyyy"
                      selected={toDisplayDate(field.value)}
                      onChange={(date: Date | null) => {
                        if (date) {
                          field.onChange({
                            month: date.getMonth() + 1,
                            year: date.getFullYear(),
                          })
                        } else {
                          field.onChange(null)
                        }

                        void trigger(`cashFlows.${index}.end`)
                      }}
                      onBlur={field.onBlur}
                    />
                  </div>
                  <InputError error={error} />
                </>
              )
            }}
          />
        </div>
        {frequencyValue !== "one-time" && (
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-2">
            <label htmlFor={`flow-${index}-end`} className="col-form-label">
              End
              <InfoPopover content="Flow includes this month." />
            </label>
            <Controller
              name={`cashFlows.${index}.end`}
              control={control}
              // AIDEV-NOTE: react-hook-form owns this render-arg shape; making it readonly
              // makes `error` unassignable to InputError.
              // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
              render={(controllerProps: CashFlowEndRenderArgs) => {
                const {
                  field,
                  fieldState: { error },
                } = controllerProps
                return (
                  <>
                    <div className={`input-group ${error ? "is-invalid" : ""}`}>
                      <DatePicker
                        id={`flow-${index}-end`}
                        // AIDEV-NOTE: react-datepicker hands back its own component instance here.
                        // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
                        ref={(elem: DatePicker | null) => {
                          if (elem) {
                            field.ref(elem.input)
                          }
                        }}
                        className={`form-control ${error ? "is-invalid" : ""}`}
                        dateFormat="MM/yyyy"
                        showMonthYearPicker
                        autoComplete="off"
                        placeholderText="mm/yyyy"
                        selected={toDisplayDate(field.value ?? null)}
                        onChange={(date: Date | null) => {
                          if (date) {
                            field.onChange({
                              month: date.getMonth() + 1,
                              year: date.getFullYear(),
                            })
                          } else {
                            field.onChange(null)
                          }
                        }}
                        onBlur={field.onBlur}
                      />
                    </div>
                    <InputError error={error} />
                  </>
                )
              }}
            />
          </div>
        )}
      </div>
      <div className="row">
        <div className="col-lg-3 col-md-4 col-sm-6 col-12 py-3">
          <button
            type="button"
            className="btn btn-secondary remove-cash-flow"
            onClick={() => {
              remove(index)
            }}
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  )
}

export default CashFlow
